import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import {memo, useMemo} from 'react'

import {usePermission} from '../../../hooks/usePermission'
import {getProjectsHash} from '../../../selectors'
import type {Mute} from '../../../services/rest'
import type {BuildTypeId, ProjectId} from '../../../types'
import {Permission, stringifyId} from '../../../types'
import {noop} from '../../../utils/empty'
import IconButton from '../IconButton/IconButton'
import {getFilteredEntities} from '../Investigations/Investigations.utils'
import Popup from '../Popup/Popup.lazy'

import MuteDetails from './MuteDetails'

import styles from './Mutes.css'

import {useAppSelector} from 'src/hooks/react-redux'

const directions = [Directions.BOTTOM_RIGHT, Directions.TOP_RIGHT]

export type Props = {
  className?: string
  iconClassName?: string
  projectId: ProjectId | null | undefined
  buildTypeId: BuildTypeId | null | undefined
  currentlyMuted: boolean | null | undefined
  mute: Mute | null | undefined
  currentlyMutes: ReadonlyArray<Mute>
  muteHandler?: () => unknown
}

function Mutes({
  className,
  iconClassName,
  projectId,
  buildTypeId,
  currentlyMuted,
  mute,
  currentlyMutes,
  muteHandler = noop,
}: Props) {
  const hasPermission = usePermission(Permission.MANAGE_BUILD_PROBLEMS, projectId)
  const projectsHash = useAppSelector(getProjectsHash)
  const filteredCurrentlyMutes = useMemo(
    () => getFilteredEntities(projectsHash, currentlyMutes, projectId, buildTypeId),
    [buildTypeId, currentlyMutes, projectId, projectsHash],
  )

  const mutes = useMemo(
    () => filteredCurrentlyMutes.filter(currentlyMute => currentlyMute.id !== mute?.id),
    [mute, filteredCurrentlyMutes],
  )

  return useMemo(() => {
    if (!currentlyMuted && mute == null) {
      return null
    }

    const anchor = (
      <IconButton
        className={classNames(iconClassName, styles.anchor, {
          [styles.currentlyMutedAnchor]: currentlyMuted,
        })}
        iconWrapperClassName={styles.iconWrapper}
        icon="mute"
        aria-label="mute"
        title=""
      />
    )
    return (
      <div className={classNames(className, styles.wrapper)} data-mute>
        <Dropdown anchor={anchor} hoverMode>
          <Popup
            className={classNames(styles.popup, {
              [styles.hasPermission]: hasPermission,
              [styles.withSeveralMutes]: (mute != null && mutes.length > 0) || mutes.length > 1,
            })}
            directions={directions}
          >
            <div className={styles.body}>
              <div className={styles.content}>
                {mute != null && (
                  <MuteDetails
                    className={styles.muteDetails}
                    buildTypeId={buildTypeId}
                    mute={mute}
                  />
                )}
                {mutes.map(currentlyMute =>
                  currentlyMute != null ? (
                    <MuteDetails
                      key={stringifyId(currentlyMute.id)}
                      className={styles.muteDetails}
                      buildTypeId={buildTypeId}
                      mute={currentlyMute}
                      currentlyMuted
                    />
                  ) : null,
                )}
              </div>
              {hasPermission && (
                <div className={styles.footer}>
                  <Button className={styles.actionButton} onClick={muteHandler}>
                    {currentlyMuted ? 'Unmute...' : 'Mute...'}
                  </Button>
                </div>
              )}
            </div>
          </Popup>
        </Dropdown>
      </div>
    )
  }, [
    buildTypeId,
    className,
    currentlyMuted,
    hasPermission,
    mute,
    muteHandler,
    mutes,
    iconClassName,
  ])
}

export default memo(Mutes)
